import React, { useState, useEffect, useMemo } from 'react';
import { Link } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import { usePlantData } from '../../../components/data/PlantData';
import SidebarPageLayout from '../../../components/layout/SidebarPageLayout';
import Marquee from '../../../components/layout/Marquee';
import Breadcrumbs from '../../../components/layout/Breadcrumbs';
import ChartTooltip from '../../../components/layout/ChartTooltip';
import DayRangePicker from '../../../components/layout/DayRangePicker';
import DataQualityPanel from '../../../components/layout/panels/DataQualityPanel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrochip } from '@fortawesome/free-solid-svg-icons';
import { apiFetch } from '../../../api/apiClient';

const DATA_QUALITY_PANELS = Object.freeze([
  { id: 'siteMeters', title: 'SITE METERS', type: 'site-meters' },
  { id: 'inverters', title: 'INVERTERS', type: 'inverters' },
  { id: 'batteryRacks', title: 'BATTERY RACKS', type: 'battery-racks' },
  { id: 'dcdcConverters', title: 'DC/DC CONVERTERS', type: 'dcdc-converters' },
  { id: 'weatherStations', title: 'WEATHER STATIONS', type: 'weather-stations' },
  { id: 'solarTrackers', title: 'SOLAR TRACKERS', type: 'solar-trackers' },
  { id: 'bessAuxLoadMeters', title: 'BESS AUX LOAD METERS', type: 'bess-aux-load-meters' },
]);

function toIsoDateParam(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function DataQuality() {
  const { plant: gemini } = usePlantData();
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });
  const [chartRange, setChartRange] = useState(() => {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 4),
      end: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999),
    };
  });
  const [qualityByType, setQualityByType] = useState({});
  const [loadingByType, setLoadingByType] = useState({});
  const [errorByType, setErrorByType] = useState({});

  const rangeKey = useMemo(() => {
    const start = toIsoDateParam(chartRange?.start);
    const end = toIsoDateParam(chartRange?.end);
    return start && end ? `${start}|${end}` : null;
  }, [chartRange]);

  useEffect(() => {
    document.title = "Vivosol Plant Performance Insights Hub | Gemini / Diagnostics / Data Quality";
  }, []);

  useEffect(() => {
    if (!rangeKey) return undefined;

    const [start, end] = rangeKey.split('|');
    let cancelled = false;

    const initialLoading = Object.fromEntries(
      DATA_QUALITY_PANELS.map((panel) => [panel.type, true]),
    );
    setLoadingByType(initialLoading);
    setErrorByType({});

    (async () => {
      try {
        // One aggregate request — backend caches per type and runs panels in parallel.
        const all = await apiFetch(
          `/api/data/quality?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
        );
        if (cancelled) return;

        const nextQuality = {};
        const nextErrors = {};
        for (const panel of DATA_QUALITY_PANELS) {
          const data = all?.[panel.type];
          if (data != null && typeof data === 'object' && data.error == null) {
            nextQuality[panel.type] = data;
          } else {
            nextErrors[panel.type] = data?.error || 'Failed to load';
          }
        }
        setQualityByType(nextQuality);
        setErrorByType(nextErrors);
      } catch (err) {
        if (cancelled) return;
        console.warn('Data quality fetch failed:', err);
        setErrorByType(
          Object.fromEntries(
            DATA_QUALITY_PANELS.map((panel) => [panel.type, err?.message || 'Failed to load']),
          ),
        );
      } finally {
        if (cancelled) return;
        setLoadingByType(
          Object.fromEntries(DATA_QUALITY_PANELS.map((panel) => [panel.type, false])),
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [rangeKey]);

  return (
    <SidebarPageLayout>
      <div className="content p-5">
        <ChartTooltip tooltip={tooltip} transition={false} />
        <Marquee />
        <h1 className="mt-3 mb-0">
          <Link to={`${gemini.dashboard || '#'}`}>
            <strong>{gemini.plant_name || 'Plant'}</strong>
          </Link>
          {' '}
          : {gemini.config} ({gemini.location})
        </h1>
        <Breadcrumbs labels={{ portfolioName: gemini.plant_name }} />
        <div className="d-flex align-items-center gap-3 mb-3">
          <h4 className="mt-0 mb-0 d-inline-block">
            <FontAwesomeIcon icon={faMicrochip} /> Diagnostics - Data Quality - Start / End Date:
          </h4>
          <DayRangePicker range={chartRange} setRange={setChartRange} />
        </div>
        {DATA_QUALITY_PANELS.map((panel) => (
          <DataQualityPanel
            key={panel.id}
            id={panel.id}
            title={panel.title}
            chartRange={chartRange}
            onTooltip={setTooltip}
            timeZone={gemini.timezone}
            metrics={qualityByType[panel.type] ?? null}
            loading={Boolean(loadingByType[panel.type])}
            error={errorByType[panel.type] ?? null}
          />
        ))}
      </div>
    </SidebarPageLayout>
  );
}

export default DataQuality;
